import { ActorProfileDetailFragmentFragment } from '@lopo-tech/lopo-api'
import React, { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useChangeAppLocation } from 'src/actions/app-state/app-state_hooks'
import MasonryFeed from 'src/components/feed/layouts/masonry_feed/masonry_feed'
import LoadingSpinnerWrapper from 'src/components/loading_spinner/loading_spinner_wrapper'
import ProfileInfo from 'src/components/profile/profile_info'
import FeedWrapper from 'src/containers/smart_components/list_wrapper/feed_wrapper'
import { textResources } from 'src/lang/de'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { useSelectCurrentProfileId } from 'src/reducers/user/user_hooks'
import ContentBlockerInfo from '../content_blocker_info/content_blocker_info'

interface IProps {
  getNewsfeedRoute: () => string;
  getNextPage: () => void;
  isLoading: boolean;
  profile?: ActorProfileDetailFragmentFragment | null;
}

const useGetRenderProfileInfo = (profile?: ActorProfileDetailFragmentFragment | null) => {
  const changeAppLocation = useChangeAppLocation()
  const history = useHistory()
  const locationShape = useGetAppLocationShape()

  const { currentProfileId } = useSelectCurrentProfileId()

  const isCurrentProfile = currentProfileId === profile?.id

  const renderProfileInfo = useCallback((): JSX.Element => {
    if (!profile) {
      return <div key='-1'></div>
    }

    return (
      <div key="profile-info">
        <ProfileInfo
          history={history}
          locationShapeId={locationShape?.id}
          changePosition={changeAppLocation}
          isCurrentProfile={isCurrentProfile}
          profile={profile}
          isMessengerEnabled={false}
        />
      </div>
    )
  }, [history, locationShape?.id, changeAppLocation, isCurrentProfile, profile])

  return renderProfileInfo
}

useGetRenderProfileInfo.displayName = 'useGetRenderProfileInfo'

const ProfileFeed: React.FC<IProps> = ({ getNextPage, isLoading, profile }) => {
  const isBlurred = !profile?.canShow?.value

  const extraElements = useMemo(
    () =>
      isBlurred
        ? [
          <ContentBlockerInfo
            key="content-blocker-info"
            customContents={{
              noUserProfile: {
                body: textResources.profile.blurredLoggedOutBody,
                bodyTitle:
                    profile?.name &&
                    textResources.profile.blurredLoggedOutBodyTile(
                      profile.name
                    ),
                title: profile?.name
              }
            }}
          />
        ]
        : undefined,
    [isBlurred, profile?.name]
  )

  const renderProfileInfo = useGetRenderProfileInfo(profile)

  return (
    <LoadingSpinnerWrapper isLoading={isLoading}>
      <FeedWrapper
        getNextPage={getNextPage}
        initialLoad={false}
      >
        <MasonryFeed
          items={profile?.contentItems.items ?? []}
          renderPostCreate={renderProfileInfo}
          isLoading={isLoading}
          showEmptyFeed={!isBlurred}
          extraElements={extraElements}
        />
      </FeedWrapper>
    </LoadingSpinnerWrapper>
  )
}

ProfileFeed.displayName = 'ProfileFeed'

export default ProfileFeed
